import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {isInsidePane} from '../utils/nodePlacement';

const DRAG_THRESHOLD = 5;

const GRAB_CLAMP_MARGIN = 16;

interface Gesture<T> {
    item: T;
    startX: number;
    startY: number;
    pointerId: number;
    target: HTMLElement;
    grabOffset: {x: number; y: number};
    active: boolean;
}

interface DragHandlers {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => void;
    onPointerCancel: (event: React.PointerEvent<HTMLElement>) => void;
}

export const useNodeDragToCanvas = <T, >(
    onDrop: (item: T, client: {x: number; y: number}) => void,
) => {
    const [draggedItem, setDraggedItem] = useState<T | null>(null);
    const previewRef = useRef<HTMLDivElement | null>(null);
    const gestureRef = useRef<Gesture<T> | null>(null);
    const pointerRef = useRef({x: 0, y: 0});
    const suppressClickRef = useRef(false);
    const onDropRef = useRef(onDrop);

    useEffect(() => {
        onDropRef.current = onDrop;
    }, [onDrop]);

    const paintPreview = () => {
        const preview = previewRef.current;
        const gesture = gestureRef.current;
        if (!preview) return;

        const grab = gesture?.grabOffset ?? {x: 0, y: 0};
        const offsetX = Math.min(grab.x, Math.max(preview.offsetWidth - GRAB_CLAMP_MARGIN, 0));
        const offsetY = Math.min(grab.y, Math.max(preview.offsetHeight - GRAB_CLAMP_MARGIN, 0));

        preview.style.transform = `translate3d(${pointerRef.current.x - offsetX}px, ${pointerRef.current.y - offsetY}px, 0)`;
    };

    useLayoutEffect(paintPreview);

    const endGesture = useCallback((commit: boolean) => {
        const gesture = gestureRef.current;
        gestureRef.current = null;
        if (!gesture) return;

        if (gesture.target.hasPointerCapture?.(gesture.pointerId)) {
            gesture.target.releasePointerCapture(gesture.pointerId);
        }

        if (!gesture.active) return;

        const previewRect = previewRef.current?.getBoundingClientRect();
        const dropPoint = previewRect
            ? {x: previewRect.left + previewRect.width / 2, y: previewRect.top + previewRect.height / 2}
            : {...pointerRef.current};

        setDraggedItem(null);

        if (commit && isInsidePane(dropPoint.x, dropPoint.y)) {
            onDropRef.current(gesture.item, dropPoint);
        }
    }, []);

    const getDragHandlers = useCallback((item: T): DragHandlers => ({
        onPointerDown: event => {
            if (event.button !== 0) return;

            suppressClickRef.current = false;
            pointerRef.current = {x: event.clientX, y: event.clientY};

            const rowRect = event.currentTarget.getBoundingClientRect();
            gestureRef.current = {
                item,
                startX: event.clientX,
                startY: event.clientY,
                pointerId: event.pointerId,
                target: event.currentTarget,
                grabOffset: {x: event.clientX - rowRect.left, y: event.clientY - rowRect.top},
                active: false,
            };
        },

        onPointerMove: event => {
            const gesture = gestureRef.current;
            if (!gesture || gesture.pointerId !== event.pointerId) return;

            pointerRef.current = {x: event.clientX, y: event.clientY};

            if (!gesture.active) {
                const travelled = Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY);
                if (travelled < DRAG_THRESHOLD) return;

                gesture.active = true;
                suppressClickRef.current = true;
                try {
                    gesture.target.setPointerCapture(event.pointerId);
                } catch {
                    // Pointer capture may be unavailable after the target is detached.
                }
                setDraggedItem(gesture.item);
                return;
            }

            paintPreview();
        },

        onPointerUp: event => {
            if (gestureRef.current?.pointerId !== event.pointerId) return;
            endGesture(true);
        },

        onPointerCancel: () => endGesture(false),
    }), [endGesture]);

    const consumeClickSuppression = useCallback(() => {
        if (!suppressClickRef.current) return false;
        suppressClickRef.current = false;
        return true;
    }, []);

    return {draggedItem, previewRef, getDragHandlers, consumeClickSuppression};
};
